// Menu de contexto flutuante (botão direito). Renderiza no body via fixed
// positioning na coordenada do clique. Fecha em: click fora, ESC, scroll.
// Pedido 2026-05-24 ("começar a colocar algumas coisas para funcionar com
// click de botão direito do mouse, principalmente no Meu Acervo de Bancos").
//
// Uso:
//   onContextMenu={(e) => openContextMenu(e, [
//     { label: "Renomear", icon: <Edit3/>, onSelect: () => ... },
//     { label: "Excluir", icon: <Trash2/>, danger: true, onSelect: ... }
//   ])}
//
// Lembre-se: chame `e.preventDefault()` no handler nativo pra suprimir o
// menu do browser. `openContextMenu` já faz isso.

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";

export interface ContextMenuItem {
  label: string;
  /** Ícone opcional renderizado à esquerda. Aceita qualquer JSX. */
  icon?: ComponentChildren;
  /** Quando true, item fica vermelho (ação destrutiva). */
  danger?: boolean;
  /** Quando true, item fica desabilitado (visualmente cinza, click ignorado).
   *  Útil pra mostrar "em breve" ou ações inaplicáveis sem esconder. */
  disabled?: boolean;
  /** Tooltip — útil pra explicar disabled. */
  hint?: string;
  /** Callback ao clicar. Menu fecha automaticamente antes de chamar.
   *  Ignorado quando `submenu` está presente (clicar abre o submenu). */
  onSelect?: () => void;
  /** Quando set, clicar substitui os itens deste menu pelos itens do submenu
   *  (mantendo posição). Mostra "▸" à direita do label. Função lazy é
   *  chamada na hora do click pra refletir state mais recente (ex: pastas
   *  recém-criadas). */
  submenu?: ContextMenuEntry[] | (() => ContextMenuEntry[]);
}

export interface ContextMenuSeparator {
  separator: true;
}

export type ContextMenuEntry = ContextMenuItem | ContextMenuSeparator;

interface CtxState {
  x: number;
  y: number;
  items: ContextMenuEntry[];
}

const ctxMenu = signal<CtxState | null>(null);

export function openContextMenu(e: MouseEvent, items: ContextMenuEntry[]) {
  e.preventDefault();
  e.stopPropagation();
  // Sem itens válidos = não abre (chamada defensiva).
  if (items.length === 0) return;
  ctxMenu.value = { x: e.clientX, y: e.clientY, items };
}

/** Variante sem MouseEvent — usada pra long-press (clicar e segurar 1.5s),
 *  que não tem um event nativo de contexto. Recebe coordenadas direto. */
export function openContextMenuAt(
  x: number,
  y: number,
  items: ContextMenuEntry[]
) {
  if (items.length === 0) return;
  ctxMenu.value = { x, y, items };
}

export function closeContextMenu() {
  ctxMenu.value = null;
}

/** Renderizar UMA vez no topo do explorer/app. Lê o signal e mostra o menu
 *  quando aberto. Múltiplas instâncias são seguras mas redundantes. */
export function ContextMenuOverlay() {
  const state = ctxMenu.value;

  useEffect(() => {
    if (!state) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeContextMenu();
      }
    }
    function onScroll() {
      closeContextMenu();
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [state]);

  if (!state) return null;

  // Clamp pra não vazar da viewport. Largura estimada 220px, altura 36px por
  // item — suficiente pro menu não cortar nos cantos.
  const estimatedW = 220;
  const estimatedH =
    state.items.reduce((acc, it) => acc + ("separator" in it ? 9 : 36), 0) + 8;
  const x = Math.min(state.x, window.innerWidth - estimatedW - 8);
  const y = Math.min(state.y, window.innerHeight - estimatedH - 8);

  return (
    <>
      {/* Backdrop transparente captura click-fora e fecha. */}
      <div
        class="fixed inset-0 z-[90]"
        onClick={closeContextMenu}
        onContextMenu={(e) => {
          e.preventDefault();
          closeContextMenu();
        }}
      />
      <div
        class="fixed z-[91] min-w-[200px] overflow-hidden rounded-md border border-border bg-popover py-1 text-sm shadow-2xl"
        style={{ left: `${x}px`, top: `${y}px` }}
        role="menu"
      >
        {state.items.map((it, i) => {
          if ("separator" in it) {
            return <div key={`sep-${i}`} class="my-1 h-px bg-border" />;
          }
          const item = it as ContextMenuItem;
          const hasSubmenu = !!item.submenu;
          return (
            <button
              key={`it-${i}`}
              type="button"
              disabled={item.disabled}
              title={item.hint}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled) return;
                if (hasSubmenu) {
                  // Substitui in-place sem fechar o overlay (mantém posição).
                  const next = typeof item.submenu === "function" ? item.submenu() : item.submenu!;
                  ctxMenu.value = { x: state.x, y: state.y, items: next };
                  return;
                }
                closeContextMenu();
                item.onSelect?.();
              }}
              class={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                item.disabled
                  ? "cursor-not-allowed text-muted-foreground/50"
                  : item.danger
                    ? "text-destructive hover:bg-destructive/10"
                    : "text-foreground hover:bg-accent"
              }`}
              role="menuitem"
            >
              {item.icon && (
                <span class="grid size-4 shrink-0 place-items-center">
                  {item.icon}
                </span>
              )}
              <span class="flex-1 truncate">{item.label}</span>
              {hasSubmenu && (
                <span class="text-xs text-muted-foreground" aria-hidden>
                  ›
                </span>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
